import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

interface Order {
  id: string;
  destination_country: string;
  destination_city: string;
  total_cartons: number;
  total_amount: number;
  created_at: string;
}

export default function OrderTicker() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .in('status', ['confirmed', 'shipped', 'completed'])
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-80 flex items-center justify-center bg-white rounded-lg border border-gray-200">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center bg-white rounded-lg border border-gray-200">
        <p className="text-gray-500">No recent orders.</p>
      </div>
    );
  }

  const doubledOrders = [...orders, ...orders];

  return (
    <div className="relative h-80 bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none"></div>
      
      <div className="h-full overflow-hidden py-4">
        <div className="animate-scroll-up">
          {doubledOrders.map((order, index) => (
            <div
              key={`${order.id}-${index}`}
              className="px-6 py-3 border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-green-100 rounded-full mt-1">
                  <i className="ri-shopping-cart-line text-green-600"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 leading-relaxed">
                    Order to <strong>{order.destination_country}</strong>, {order.destination_city} · {order.total_cartons} cartons · <strong>${order.total_amount.toFixed(2)}</strong>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(order.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
